import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, getProfile } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';
import { Shield as ShieldIcon } from '@mui/icons-material';

interface PrivateRouteProps {
  element: React.ReactElement;
}

export default function PrivateRoute({ element }: PrivateRouteProps) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadProfile = async () => {
      if (isAuthenticated) {
        try {
          await dispatch(getProfile()).unwrap();
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      }
      // Добавляем небольшую задержку для плавности
      setTimeout(() => setIsLoading(false), 300);
    };
    
    loadProfile();
  }, [isAuthenticated, dispatch]);
  
  if (isLoading) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              animation: 'pulse 4s ease-in-out infinite',
            },
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress
                size={80}
                thickness={3}
                sx={{
                  color: 'white',
                  position: 'absolute',
                }}
              />
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShieldIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 600,
                textAlign: 'center',
                animation: 'fadeInOut 2s ease-in-out infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.7 },
                  '50%': { opacity: 1 },
                },
              }}
            >
              Проверка доступа...
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }
  
  if (!isAuthenticated) {
    // Перенаправляем на страницу входа, сохраняя текущий URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return element;
}